/**
 * Static metadata for the 39 polled `s7DbMap` symbols + 1 alarm-rollup
 * placeholder, grouped into the 7 categories per EDGE_LIVE_MONITOR_SPEC.md
 * §3.2.1.
 *
 * Line-3 labels are quoted verbatim from SBL500_DB_MAP.md §5 (the SDF symbol
 * comments — bilingual EN/Slovak from Trens). The labels are NOT i18n-translated
 * — internal tooling, English chrome, with the SDF labels carried as-is.
 *
 * `kind`:
 *   - "bool" — boolean, decoded by edge agent post-invert.
 *   - "number" — numeric (REAL or INT).
 *   - "placeholder" — dashed-border tile, always renders "—" (e.g.
 *     activeProgram on 810D Sharp7, top alarm code pending rollup).
 *
 * `semantic`:
 *   - "good-when-true": bool true = GOOD (e.g. cncReady, programRunning,
 *     hydraulicsPressureOk).
 *   - "bad-when-true": bool true = BAD, triggers Zone 1 banner + terracotta tile
 *     (e.g. nckAlarmActive, coolantCoversBad).
 *   - "bad-when-false": bool false = BAD (post-invert bool where the agent
 *     already inverted, e.g. eStopOk = false means E-stop is pressed).
 *   - "info": informational bool — neither good nor bad (chuck open/closed,
 *     mode flag, button presses, cycle/coolant requested, etc).
 *   - "number": numeric — never has a semantic colour.
 *
 * `inverted`: matches `_invert: true` in the live `s7DbMap` JSON. Edge Agent
 * inverts before publish — page does NOT invert again. The `inv` glyph
 * appears in line 1 next to the address for transparency.
 */

export type TileKind = "bool" | "number" | "placeholder";
export type TileSemanticKey =
  | "good-when-true"
  | "bad-when-true"
  | "bad-when-false"
  | "info"
  | "number";

export interface SignalDef {
  /** `s7DbMap` key — used to look up the value from MachineStatusDto.signals. */
  key: string;
  /** Tile line 1 — uppercased symbol name (with underscores, monospace). */
  symbol: string;
  /** Tile line 1 right side — PLC address (e.g. `I 34.0`, `DB10.106.1`). */
  address: string;
  /** Optional address suffix (e.g. `REAL`, `INT`) shown below the address. */
  addressSuffix?: string;
  /** Tile line 3 — Slovak+English label quoted from the SDF. */
  label: string;
  kind: TileKind;
  semantic: TileSemanticKey;
  /** `_invert: true` in s7DbMap. Renders `inv` glyph next to address. */
  inverted?: boolean;
  /** For numbers — caption next to value (e.g. `rpm`, `M-fn`). */
  unit?: string;
  /** For placeholders — text rendered in line 2. */
  placeholderText?: string;
  /** For placeholders — line 3 hint. */
  placeholderHint?: string;
  /** Mode flag — only one is "active" at a time. Used in §3.2.3. */
  modeFlag?: boolean;
}

export interface SignalSection {
  /** Section heading number in Zone 2 (1..7). */
  num: number;
  /** Section heading text (uppercased). */
  heading: string;
  /** Reference to MACHINE_CONNECTIVITY_VISION.md anchor (e.g. `§3.1`). */
  visionRef: string;
  /** Optional right-aligned section subtitle (e.g. `2 SIGNALS`). */
  subtitle?: string;
  signals: SignalDef[];
}

export const SECTIONS: SignalSection[] = [
  {
    num: 1,
    heading: "OPERATIONAL STATE",
    visionRef: "Vision §3.1",
    signals: [
      {
        key: "cncReady",
        symbol: "CNC_READY",
        address: "M 102.0",
        label: "CNC Ready (Trens)",
        kind: "bool",
        semantic: "good-when-true",
      },
      {
        key: "ncReady",
        symbol: "NC_READY",
        address: "DB10.108.7",
        label: "NCK CPU ready",
        kind: "bool",
        semantic: "good-when-true",
      },
      {
        key: "programRunning",
        symbol: "PROGRAM_RUNNING",
        address: "DB21.35.0",
        label: "Program running (Chan1)",
        kind: "bool",
        semantic: "good-when-true",
      },
      {
        key: "feedHold",
        symbol: "FEED_HOLD",
        address: "M 40.1",
        label: "Feed hold",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "feedStop",
        symbol: "FEED_STOP",
        address: "M 40.0",
        label: "Feed STOP",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "spindleStop",
        symbol: "SPINDLE_STOP",
        address: "M 50.0",
        label: "Spindle STOP",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "spindleStationary",
        symbol: "SPINDLE_STATIONARY",
        address: "DB33.61.4",
        label: "n=0 spindle (Trens FC 77)",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "modeAuto",
        symbol: "MODE_AUTO",
        address: "M 9.2",
        label: "AUTO mode",
        kind: "bool",
        semantic: "info",
        modeFlag: true,
      },
      {
        key: "modeMda",
        symbol: "MODE_MDA",
        address: "M 9.1",
        label: "MDA mode",
        kind: "bool",
        semantic: "info",
        modeFlag: true,
      },
      {
        key: "modeJog",
        symbol: "MODE_JOG",
        address: "M 9.0",
        label: "JOG mode",
        kind: "bool",
        semantic: "info",
        modeFlag: true,
      },
      {
        key: "modeRef",
        symbol: "MODE_REF",
        address: "M 9.4",
        label: "REF mode",
        kind: "bool",
        semantic: "info",
        modeFlag: true,
      },
      {
        key: "machineReady",
        symbol: "MACHINE_READY",
        address: "M 60.0",
        label: "Zákl. podm. stroja pre UP / Machine ready",
        kind: "bool",
        semantic: "good-when-true",
      },
    ],
  },
  {
    num: 2,
    heading: "SAFETY / INTERLOCKS",
    visionRef: "Vision §3.5",
    signals: [
      {
        key: "doorClosed",
        symbol: "DOOR_CLOSED",
        address: "I 34.0",
        label: "Door close (Trens)",
        kind: "bool",
        semantic: "good-when-true",
      },
      {
        key: "doorPressureOk",
        symbol: "DOOR_PRESSURE_OK",
        address: "I 35.6",
        label: "Tlak vzduchu pneum. dveri / Door air OK",
        kind: "bool",
        semantic: "good-when-true",
      },
      {
        key: "eStopOk",
        symbol: "E_STOP_OK",
        address: "DB10.106.1",
        label: "E-STOP not pressed (post-invert)",
        kind: "bool",
        semantic: "bad-when-false",
        inverted: true,
      },
      {
        key: "doorLockedOrOpen",
        symbol: "DOOR_LOCKED_OR_OPEN",
        address: "I 34.1",
        label: "Dvere zamknute/otvorene",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "hydraulicsLevelOk",
        symbol: "HYDRAULICS_LEVEL_OK",
        address: "I 32.4",
        label: "Hladina HA OK (post-invert)",
        kind: "bool",
        semantic: "bad-when-false",
        inverted: true,
      },
      {
        key: "hydraulicsPressureOk",
        symbol: "HYDRAULICS_PRESSURE_OK",
        address: "I 32.5",
        label: "Tlak HA OK / Hyd pressure OK",
        kind: "bool",
        semantic: "bad-when-false",
      },
      {
        key: "motorBreakersOk",
        symbol: "MOTOR_BREAKERS_OK",
        address: "I 32.3",
        label: "Istice motorov OK / Motor breakers OK",
        kind: "bool",
        semantic: "bad-when-false",
      },
      {
        key: "eStopButton",
        symbol: "E_STOP_BUTTON",
        address: "I 32.0",
        label: "Tlac.CS / E-stop button (not pressed)",
        kind: "bool",
        semantic: "info",
        inverted: true,
      },
      {
        key: "aggregatesStarted",
        symbol: "AGGREGATES_STARTED",
        address: "I 32.2",
        label: "Agregaty nastartovane / Hyd+lub running",
        kind: "bool",
        semantic: "good-when-true",
      },
    ],
  },
  {
    num: 3,
    heading: "CYCLE / PRODUCTION",
    visionRef: "Vision §3.2",
    signals: [
      {
        key: "cycleStartButton",
        symbol: "CYCLE_START_BUTTON",
        address: "I 2.1",
        label: "Tl. START Cyklu / Cycle start",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "cycleStopButton",
        symbol: "CYCLE_STOP_BUTTON",
        address: "I 2.0",
        label: "Tl. STOP Cyklu / Cycle stop",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "spindleStartButton",
        symbol: "SPINDLE_START_BUTTON",
        address: "I 2.5",
        label: "Tl. start vretena / Spindle start",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "spindleStopButton",
        symbol: "SPINDLE_STOP_BUTTON",
        address: "I 2.4",
        label: "Tl. stop vretena / Spindle stop",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "spindleM10ChuckClose",
        symbol: "SPINDLE_M10_CHUCK_CLOSE",
        address: "DB21.195.2",
        label: "M10 close chuck (MDyn)",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "spindleM11ChuckOpen",
        symbol: "SPINDLE_M11_CHUCK_OPEN",
        address: "DB21.195.3",
        label: "M11 open chuck (MDyn)",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "activeProgram",
        symbol: "ACTIVE_PROGRAM",
        address: "BTSS only",
        label: "BTSS only — Phase 2 OPC-UA",
        kind: "placeholder",
        semantic: "info",
        placeholderText: "—",
        placeholderHint: "BTSS only — Phase 2 OPC-UA",
      },
    ],
  },
  {
    num: 4,
    heading: "PROCESS VARIABLES",
    visionRef: "Vision §3.3",
    signals: [
      {
        key: "spindleSpeedRpm",
        symbol: "SPINDLE_SPEED_RPM",
        address: "DB33.92",
        addressSuffix: "REAL",
        label: "E_SFunct (commanded RPM)",
        kind: "number",
        semantic: "number",
        unit: "rpm",
      },
      {
        key: "spindleMFunction",
        symbol: "SPINDLE_M_FUNCTION",
        address: "DB33.90",
        addressSuffix: "INT",
        label: "E_MFunct (M3 spindle CW)",
        kind: "number",
        semantic: "number",
        unit: "M-fn",
      },
    ],
  },
  {
    num: 5,
    heading: "CHUCK",
    visionRef: "Vision §3.8",
    subtitle: "PROCESS-SPECIFIC",
    signals: [
      {
        key: "chuckOpen",
        symbol: "CHUCK_OPEN",
        address: "I 33.1",
        label: "Celuste otvorene (post-invert)",
        kind: "bool",
        semantic: "info",
        inverted: true,
      },
      {
        key: "chuckClosed",
        symbol: "CHUCK_CLOSED",
        address: "I 33.2",
        label: "Celuste zatvorene (post-invert)",
        kind: "bool",
        semantic: "info",
        inverted: true,
      },
      {
        key: "chuckOpenOutput",
        symbol: "CHUCK_OPEN_OUTPUT",
        address: "Q 40.5",
        label: "MG otv. celusti / Open solenoid",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "chuckCloseOutput",
        symbol: "CHUCK_CLOSE_OUTPUT",
        address: "Q 40.6",
        label: "MG zatv. celusti / Close solenoid",
        kind: "bool",
        semantic: "info",
      },
    ],
  },
  {
    num: 6,
    heading: "COOLANT",
    visionRef: "Vision §3.7",
    subtitle: "ENVIRONMENTAL",
    signals: [
      {
        key: "coolantRequested",
        symbol: "COOLANT_REQUESTED",
        address: "M 80.0",
        label: "Poziadavka chladenia / Coolant request",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "coolantActive",
        symbol: "COOLANT_ACTIVE",
        address: "M 80.3",
        label: "CHladenie aktivovane / Coolant active",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "coolantContactor",
        symbol: "COOLANT_CONTACTOR",
        address: "Q 40.4",
        label: "Stykac CHL / Coolant contactor",
        kind: "bool",
        semantic: "info",
      },
      {
        key: "coolantCoversBad",
        symbol: "COOLANT_COVERS_BAD",
        address: "M 80.4",
        label: "Kryty nie su zatv. (diag)",
        kind: "bool",
        semantic: "bad-when-true",
      },
    ],
  },
  {
    num: 7,
    heading: "ALARMS",
    visionRef: "Vision §3.4",
    signals: [
      {
        key: "topAlarmCode",
        symbol: "TOP_ALARM_CODE",
        address: "ROLLUP",
        label: "(pending SBL500-EDGE-ALARM-ROLLUP)",
        kind: "placeholder",
        semantic: "info",
        placeholderText: "—",
        placeholderHint: "(pending SBL500-EDGE-ALARM-ROLLUP)",
      },
      {
        key: "nckAlarmActive",
        symbol: "NCK_ALARM_ACTIVE",
        address: "DB10.109.0",
        label: "NCK alarm present (system-wide)",
        kind: "bool",
        semantic: "bad-when-true",
      },
    ],
  },
];

/** Total signal count across all sections (for dev sanity check). */
export const TOTAL_SIGNAL_COUNT = SECTIONS.reduce(
  (sum, s) => sum + s.signals.length,
  0
);
